// src/components/ui/Dropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * A reusable Dropdown component.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.trigger - The element that triggers the dropdown (e.g., a Button).
 * @param {React.ReactNode} props.children - The content to be displayed inside the dropdown menu.
 * @param {'left' | 'right'} [props.align='right'] - Alignment of the dropdown content relative to the trigger.
 * @param {string} [props.className=''] - Additional CSS classes for the dropdown content container.
 */
const Dropdown = ({ trigger, children, align = 'right', className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const dropdownClasses = `
        absolute mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-[60] // Increased z-index here
        ${align === 'left' ? 'left-0' : 'right-0'}
        ${className}
    `;

    const variants = {
        hidden: { opacity: 0, scale: 0.95, y: -10 },
        visible: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: -10 },
    };

    return (
        <div className="relative inline-block text-left">
            <div ref={triggerRef} onClick={toggleDropdown} className="cursor-pointer">
                {trigger}
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        ref={dropdownRef}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={variants}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        className={dropdownClasses}
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Dropdown;